import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';
import './BrandOnboardingProgressBar.scss';

import Tooltip from '../General/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faWarning } from '@fortawesome/pro-regular-svg-icons';
import { brandOnboardingPanelIsViewable, brandOnboardingPanelHasBeenViewed } from '../../Helpers/onboarding_helpers';

const BrandOnboardingProgressBar = props => {
  const { onboarding, sections, activeSectionName, activePanelName } = props;
  if (!sections) return null;

  const panelIsCompleteAndHasBeenViewed = panel => panel.isComplete && brandOnboardingPanelHasBeenViewed(onboarding, panel.name);

  const currentSection = sections.find(s => s.name === activeSectionName);
  const currentSectionIndex = sections.findIndex(s => s.name === activeSectionName);
  const completionStats = useMemo(() => {
    let totalPanels = 0;
    let completedPanels = 0;

    sections.forEach(section => {
      const viewablePanels = section.panels.filter(brandOnboardingPanelIsViewable);
      totalPanels += viewablePanels.length;
      completedPanels += viewablePanels.filter(panelIsCompleteAndHasBeenViewed).length;
    });

    return { totalPanels, completedPanels, percentComplete: ((completedPanels / totalPanels) * 100).toFixed(0) };
  }, [sections]);
  const currentSectionVisiblePanels = currentSection ? currentSection.panels.filter(brandOnboardingPanelIsViewable) : [];

  const getPanelNumber = panel => {
    const { name } = panel;
    let panelNumber = 0;

    for (const section of sections) {
      for (const panel of section.panels.filter(brandOnboardingPanelIsViewable)) {
        ++panelNumber;
        if (panel.name === name) return panelNumber;
      }
    }

    return -1;
  };

  return (
    <div className='brand-onboarding-progress-bar-outer'>
      <div className='brand-onboarding-progress-bar-inner'>
        <div className='brand-onboarding-progress-bar-header'>
          <div className='brand-onboarding-progress-bar-title'>Brand Onboarding</div>
          <div className='brand-onboarding-progress-bar-percentage'>{completionStats.percentComplete}% Completed</div>
        </div>

        <div className='brand-onboarding-progress-bar-sections'>
          {sections.map((section, index) => {
            const sectionViewablePanels = section.panels.filter(brandOnboardingPanelIsViewable);
            const isPreviousSection = index < currentSectionIndex;
            const firstIncompletePanel = sectionViewablePanels.find(panel => !panel.isComplete);
            const firstPanel = sectionViewablePanels[0];
            const percentSectionCompletion =
              (sectionViewablePanels.filter(panel => panelIsCompleteAndHasBeenViewed(panel)).length / sectionViewablePanels.length) * 100;

            return (
              <div
                key={`Section - ${section.name}`}
                className='brand-onboarding-progress-bar-section'
                onClick={() => {
                  firstIncompletePanel ? props.goToPanel(section.name, firstIncompletePanel.name) : props.goToPanel(section.name, firstPanel.name);
                }}
              >
                <Tooltip
                  message={firstIncompletePanel && isPreviousSection ? `${section.name} || ${firstIncompletePanel.name} is incomplete` : section.name}
                >
                  <div className='brand-onboarding-progress-bar-section-background'></div>
                  <div
                    style={{
                      width: `${percentSectionCompletion}%`
                    }}
                    className={cn('brand-onboarding-progress-bar-section-progress', { isIncomplete: !!firstIncompletePanel })}
                  ></div>
                </Tooltip>
              </div>
            );
          })}
        </div>

        <div className='brand-onboarding-progress-bar-panels'>
          {currentSectionVisiblePanels.map((panel, index) => {
            const { isInProgress, inProgressText, name } = panel;
            const isLastPanel = index === currentSectionVisiblePanels.length - 1;
            const isActivePanel = name === activePanelName;
            if (panel.isHidden()) return null;

            const panelCompletedAndViewed = panelIsCompleteAndHasBeenViewed(panel);

            return (
              <>
                <Tooltip
                  message={inProgressText ? `${name} || ${inProgressText}` : name}
                  key={name}
                  outerClassName={cn('brand-onboarding-progress-bar-panel', { isActivePanel, isComplete: panelCompletedAndViewed, isInProgress })}
                >
                  <div onClick={() => props.goToPanel(currentSection.name, panel.name)}>
                    {panelCompletedAndViewed ? <FontAwesomeIcon icon={panelCompletedAndViewed ? faCheck : faWarning} /> : getPanelNumber(panel)}
                  </div>
                </Tooltip>
                {!isLastPanel && <div key={`Panel Spacer ${index}`} className='brand-onboarding-progress-bar-panel-spacer'></div>}
              </>
            );
          })}
        </div>
      </div>
    </div>
  );
};

BrandOnboardingProgressBar.propTypes = {
  user: PropTypes.object.isRequired,
  onboarding: PropTypes.object.isRequired,

  sections: PropTypes.array.isRequired,
  activeSectionName: PropTypes.string.isRequired,
  activePanelName: PropTypes.string.isRequired,
  goToPanel: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  onboarding: state.onboarding,
  user: state.user
});

export default connect(mapStateToProps, {})(BrandOnboardingProgressBar);
